<template>
  <component
    :is="isReadonly ? 'div' : 'button'"
    :class="computedClass"
    :aria-pressed="isSelectable ? isSelected : undefined"
    :aria-disabled="isDisabled"
    :data-testid="dataTestid"
    v-bind="$attrs"
    @click="handleClick"
    @keydown="handleKeydown"
  >
    <SbAvatar
      v-if="avatar"
      class="sb-tag__avatar"
      :src="avatar.image_url"
      :friendly-name="avatar.friendly_name"
      :name="avatar.alt"
      :data-testid="`${dataTestid}-avatar`"
    />
    <SbIcon
      v-if="icon"
      :name="icon"
      :is-filled="iconFilled"
      aria-hidden="true"
    />
    <span class="sb-tag__label" :data-testid="`${dataTestid}-label`">
      <slot>
        {{ label }}
      </slot>
    </span>
    <SbIconButton
      v-if="closable && isReadonly"
      size="small"
      label="Remove"
      :data-testid="`${dataTestid}-remove-button`"
      icon="remove"
      is-rounded
      :is-disabled="isDisabled"
      @click="onClose"
    />
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs, type PropType } from 'vue'
import { includes } from '../../utils'
import { variants, tagTypes } from './lib'
import SbAvatar from '../Avatar/SbAvatar.vue'
import SbIcon from '../Icon'
import SbIconButton from '../IconButton'
import type { TagType, UserAvatar } from './types'
import type { IconName } from '../Icon'

const props = defineProps({
  /**
   * The label of the tag.
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * The variant of the tag, which defines the tag's behavior.
   * It can be one of 'readonly', or 'selectable'.
   * Readonly tags cannot be interacted with, selectable tags can be selected.
   */
  variant: {
    type: String as PropType<'readonly' | 'selectable'>,
    default: 'readonly',
    validator: (variant, props) => {
      const hasAvatar = Object.keys(props.avatar || {}).length > 0
      const variantValid = includes(variants, variant)
      const isSelectable = variant === 'selectable'

      if (isSelectable && hasAvatar) {
        console.warn(
          'Tag cannot be selectable when avatar is provided, ensure avatar is not set',
        )
        return false
      }

      return variantValid
    },
  },
  /**
   * The type of the tag, which defines its style.
   */
  type: {
    type: String as PropType<TagType>,
    default: 'gainsboro',
    validator: (type, props) => {
      const isSelectable = props.variant === 'selectable'
      const isValidType = includes(tagTypes, type)

      if (isSelectable && type !== 'white') {
        console.warn('Tag should be set to white when selectable')
        return false
      }

      return isValidType
    },
  },
  /**
   * The user avatar, which will display the user's avatar before the label.
   */
  avatar: {
    type: Object as PropType<UserAvatar>,
    default: undefined,
  },
  /** The Icon positioned left of the label. You can use [lucide icons](https://lucide.dev/icons) or the the Storyblok icons. */
  icon: {
    type: String as PropType<IconName>,
    default: undefined,
  },
  iconFilled: {
    type: Boolean,
    default: false,
  },
  /**
   * Defines if the tag is closable or not.
   */
  closable: {
    type: Boolean,
    default: false,
  },
  /**
   * Defines if the tag is disabled or not.
   */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Defines if the tag is selected or not.
   */
  isSelected: {
    type: Boolean,
    default: false,
    validator: (_, props) => {
      if (props.variant !== 'selectable' && props.isSelected) {
        console.warn(
          'Tag cannot be selected when not selectable, ensure selectable is set to true',
        )
        return false
      }

      const hasAvatar = Object.keys(props.avatar || {}).length > 0
      if (props.selected && hasAvatar) {
        console.warn(
          'Tag cannot be selected when avatar is provided, ensure avatar is not set',
        )
        return false
      }

      return true
    },
  },
  title: {
    type: String,
    default: undefined,
  },
})

const emits = defineEmits<{
  /**
   * Emitted when the tag is clicked.
   */
  (e: 'click', event: MouseEvent): void
  /**
   * Emitted when the tag is selected.
   */
  (e: 'keydown', event: KeyboardEvent): void
  /**
   * Emitted when the tag is closed.
   * This is only emitted if the tag is closable.
   */
  (e: 'close', event: MouseEvent): void
  /**
   * Emitted when the tag's selected state is updated.
   * This is only emitted if the tag is selectable.
   */
  (e: 'update:isSelected', isSelected: boolean): void
}>()

const attrs = useAttrs()

const dataTestid = computed(() => attrs['data-testid'] || 'sb-tag')

const isAvatar = computed(
  () =>
    props.avatar &&
    Object.keys(props.avatar).length &&
    (props.avatar.friendly_name || props.avatar.image_url),
)

const computedClass = computed(() => [
  `sb-tag sb-tag--${isSelectable.value ? 'white' : props.type}`,
  {
    'sb-tag--readonly': isReadonly.value,
  },
  {
    'sb-tag--selectable': isSelectable.value,
  },
  { 'sb-tag--closable': props.closable },
  { 'sb-tag--selected': isSelectable.value && props.isSelected },
  { 'sb-tag--disabled': props.isDisabled },
  { 'sb-tag--avatar': isAvatar.value },
])

const isReadonly = computed(() => props.variant === 'readonly')
const isSelectable = computed(() => props.variant === 'selectable')

function onClose(event: MouseEvent) {
  emits('close', event)
}

const handleClick = (event: MouseEvent) => {
  if (props.isDisabled) {
    return
  }
  handleSelected()
  emits('click', event)
}

const handleSelected = () => {
  if (isSelectable.value) {
    emits('update:isSelected', !props.isSelected)
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (props.isDisabled) {
    return
  }
  emits('keydown', event)
}
</script>

<style lang="scss" src="./tag.scss" />
